import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Sparkles } from 'lucide-react';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Preview } from './pages/Preview';
import { SocialEdit } from './pages/SocialEdit';
import { Reels } from './pages/Reels';
import { LinkedIn } from './pages/LinkedIn';
import { VideoUpload } from './pages/VideoUpload';
import Trends from './pages/Trends';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { ProjectSelection } from './pages/ProjectSelection';
import { Config } from './pages/Config';
import './App.css';

const API_BASE = '/api';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth.php`, { credentials: 'include' });
      const data = await response.json();
      setAuthenticated(data.authenticated);
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (data.success) {
        setAuthenticated(true);
        setPassword('');
        toast.success('Zalogowano pomyślnie!');
      } else {
        toast.error(data.message || 'Błędne hasło');
      }
    } catch (error) {
      toast.error('Błąd logowania');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/auth.php`, { method: 'DELETE', credentials: 'include' });
      setAuthenticated(false);
      toast.success('Wylogowano');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-semibold text-muted-foreground">Ładowanie...</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="bg-card p-8 rounded-lg shadow-lg border w-96">
          <div className="flex items-center justify-center mb-6">
            <Sparkles className="w-8 h-8 text-primary mr-2" />
            <h1 className="text-2xl font-bold">Newsletter Panel</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Hasło</label>
              <input
                type="password"
                placeholder="Wprowadź hasło"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button type="submit" className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors font-medium">
              Zaloguj się
            </button>
          </form>
        </div>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ProjectProvider>
        <AppContent onLogout={handleLogout} />
        <Toaster position="top-right" richColors />
      </ProjectProvider>
    </BrowserRouter>
  );
}

function AppContent({ onLogout }) {
  const { currentProject, loading } = useProject();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="text-xl font-semibold text-muted-foreground">Ładowanie projektu...</div></div>;
  }

  if (!currentProject) {
    return <ProjectSelection />;
  }

  return (
    <Layout onLogout={onLogout}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/preview" element={<Preview />} />
        <Route path="/social" element={<SocialEdit />} />
        <Route path="/video" element={<VideoUpload />} />
        <Route path="/reels" element={<Reels />} />
        <Route path="/linkedin" element={<LinkedIn />} />
        <Route path="/trends" element={<Trends />} />
        <Route path="/config" element={<Config />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}

export default App;
